/**
 * Modèle de données du reçu de paiement.
 *
 * Même principe que le certificat de scolarité : l'établissement configure UNE
 * fois l'apparence du document, puis chaque reçu est produit en résolvant ce
 * modèle avec les données RÉELLES de l'élève et du versement concerné.
 *
 * Les trois modèles (Classique, Ticket, Moderne) et les deux formats (A4, A5)
 * reprennent exactement la maquette validée : ils ne définissent QUE
 * l'habillage visuel, le contenu reste identique quel que soit le choix.
 */

/* -------------------------------------------------------------------------
 * Palettes
 *
 * Un reçu est un document imprimé : le papier reste clair dans tous les cas,
 * seule la couleur d'accent (bandeau, en-tête de tableau, montant réglé)
 * change. « Ardoise » reproduit la maquette d'origine.
 * ---------------------------------------------------------------------- */
export const RECEIPT_PALETTES = [
  { id: "ardoise", name_fr: "Ardoise", name_en: "Slate", paper: "#ffffff", ink: "#0f172a", soft: "#64748b", accent: "#1e293b", accent_ink: "#f8fafc", line: "#e2e8f0", zone: "#f1f5f9", danger: "#dc2626" },
  { id: "officiel", name_fr: "Bleu officiel", name_en: "Official blue", paper: "#ffffff", ink: "#111d31", soft: "#55637c", accent: "#1d4e8f", accent_ink: "#f6f9ff", line: "#dfe6f1", zone: "#eff4fb", danger: "#c62828" },
  { id: "vert", name_fr: "Vert académique", name_en: "Academic green", paper: "#fdfefc", ink: "#12211a", soft: "#54685c", accent: "#1f6a45", accent_ink: "#f4fbf7", line: "#dde9e2", zone: "#eef6f1", danger: "#b3261e" },
  { id: "bordeaux", name_fr: "Bordeaux", name_en: "Burgundy", paper: "#fffcfb", ink: "#1f1315", soft: "#6f545a", accent: "#8b2333", accent_ink: "#fff6f7", line: "#eddfe1", zone: "#faf0f1", danger: "#8b2333" },
  { id: "ivoire", name_fr: "Ivoire & or", name_en: "Ivory & gold", paper: "#fffdf5", ink: "#1b1a14", soft: "#6a6350", accent: "#8a6a1c", accent_ink: "#fffdf3", line: "#eae2cd", zone: "#f7f2e3", danger: "#a1381a" },
];

/** Les trois modèles proposés : uniquement de l'habillage visuel. */
export const RECEIPT_TEMPLATES = ["classique", "ticket", "moderne"];

/**
 * Formats d'impression.
 *
 * Le modèle « Ticket » impose son propre format (rouleau 80 mm) : le choix
 * A4 / A5 ne s'applique qu'aux deux autres.
 */
export const RECEIPT_FORMATS = ["a4", "a5"];

/** Dimensions en millimètres, utilisées pour la mise à l'échelle à l'impression. */
export const RECEIPT_PAGE_SIZES = {
  a4: { width: 210, height: 297, margin: 12 },
  a5: { width: 148, height: 210, margin: 8 },
  ticket: { width: 80, height: 0, margin: 4 },
};

export function paletteOf(id) {
  return RECEIPT_PALETTES.find((p) => p.id === id) || RECEIPT_PALETTES[0];
}

/**
 * Langue courante de l'application.
 *
 * Ce fichier n'est pas un composant : il n'a pas accès à `this.$i18n`. La
 * langue est relue depuis le même emplacement que celui utilisé par main.js
 * au démarrage, ce qui garantit la cohérence sans dépendance circulaire.
 */
export function currentLocale() {
  try {
    const saved = localStorage.getItem("locale");
    return saved === "en" ? "en" : "fr";
  } catch (error) {
    return "fr";
  }
}

/*
 * Contenu rédactionnel du reçu.
 *
 * Titre, mention de bas de page et préfixe de numérotation ne sont PAS
 * configurables : ils suivent la langue de l'utilisateur et sont résolus à
 * l'émission. Les rendre modifiables aurait figé une langue dans la
 * configuration — un établissement bilingue aurait imprimé « Reçu de
 * paiement » à un parent anglophone.
 */

/** Titre du document, dans la langue de l'interface. */
export function defaultTitle(locale = currentLocale()) {
  return locale === "en" ? "Payment receipt" : "Reçu de paiement";
}

/** Fonction du signataire par défaut. */
export function defaultSignatoryRole(locale = currentLocale()) {
  return locale === "en" ? "The Accountant" : "Le Comptable";
}

/** Préfixe du numéro de reçu, commun à tous les établissements. */
export const RECEIPT_PREFIX = "REC";

/** Mention de bas de page, dans la langue de l'interface. */
export function defaultFooterNote(locale = currentLocale()) {
  return locale === "en"
    ? "This receipt certifies the payment received. Please keep it as proof of payment."
    : "Ce reçu atteste du paiement reçu. Merci de le conserver comme justificatif.";
}

/**
 * Modèle par défaut, tel qu'il sera persisté.
 *
 * `locale` ne sert qu'à la fonction du signataire, seul texte encore
 * configurable : sa valeur de départ suit la langue de celui qui configure,
 * puis reste telle qu'il l'a laissée.
 */
export function defaultReceipt(locale = currentLocale()) {
  return {
    /* --- Habillage --- */
    template: "classique",
    format: "a4",
    palette: "ardoise",

    /* --- Pied de page --- */
    signatory_role: defaultSignatoryRole(locale),
    /* Nom du signataire : vide par défaut, l'établissement le renseigne. */
    signatory_name: "",
    show_signature: true,

    /* --- Options d'affichage --- */
    show_logo: true,
    show_school_contacts: true,
  };
}

/**
 * Ramène un reçu enregistré vers la structure courante.
 *
 * Les configurations sont conservées telles quelles ; seuls les champs absents
 * reçoivent leur valeur par défaut. Rien de ce qu'une école a saisi n'est
 * perdu, et un champ ajouté plus tard ne casse pas les modèles existants.
 */
export function migrateReceipt(saved) {
  const base = defaultReceipt();
  if (!saved || typeof saved !== "object") return base;

  const merged = { ...base, ...saved };
  if (!RECEIPT_TEMPLATES.includes(merged.template)) merged.template = base.template;
  if (!RECEIPT_FORMATS.includes(merged.format)) merged.format = base.format;
  return merged;
}

/* -------------------------------------------------------------------------
 * Résolution des données réelles
 *
 * IMPORTANT : chaque champ ci-dessous est branché sur une donnée RÉELLE de
 * l'application. Aucune valeur n'est inventée — un champ absent reste vide et
 * `missing` le signale, plutôt que d'imprimer une information fausse.
 *
 *   élève      : /api/student/:id -> registration_number, first_name, last_name
 *   classe     : /api/get_classroom_details -> grade.name, number, speciality_code
 *   tranche    : student.payments[] -> id, number, label, amount, status, splits
 *   versement  : payment.splits[] -> amount | amount_paid, on | processed_on,
 *                operator_tx_id | reference composée
 *                (le POST /api/set_partial_payment envoie operator_tx_id ;
 *                la relecture ne le rend pas sous ce nom)
 *   école      : schoolProfile -> name, town, phone, email, po_box…
 * ---------------------------------------------------------------------- */

/** Première valeur réellement renseignée parmi une liste de clés. */
function pick(source, keys) {
  if (!source) return "";
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
}

/** Nombre exploitable, quel que soit le nom du champ côté API. */
function num(source, keys) {
  if (!source) return 0;
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return 0;
}

/** Montant formaté avec le séparateur de milliers de la langue courante. */
export function formatAmount(value, locale = currentLocale(), currency = "") {
  const formatted = Number(value || 0).toLocaleString(locale === "fr" ? "fr-FR" : "en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return currency ? `${currency} ${formatted}` : formatted;
}

/**
 * Date affichable.
 *
 * Les dates arrivent sous deux formes : ISO côté API (`2026-09-07…`), ou déjà
 * localisées par l'application au moment de la saisie (`07/09/2026`). SEULES
 * les premières sont converties : `new Date("07/09/2026")` serait lu comme le
 * 9 juillet par le moteur JavaScript, qui interprète ce format en mois/jour.
 * Une chaîne non ISO est donc renvoyée telle quelle — mieux vaut afficher ce
 * que l'application a enregistré qu'une date fausse.
 */
export function formatDate(value, locale = currentLocale()) {
  if (!value) return "";
  const raw = String(value).trim();
  if (!/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw;

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Niveau de la classe, sous son nom lisible.
 *
 * `grade_code` est un code interne (« CIN ») : c'est `grade.name` qui porte le
 * libellé que l'établissement reconnaît (« 5eme »), et c'est lui qui est
 * affiché partout ailleurs dans l'application. Le code ne sert que de repli,
 * préférable à un blanc sur un document officiel.
 */
function gradeLabel(classroom) {
  const direct = pick(classroom, ["name"]);
  if (direct) return direct;

  // `grade` est un objet dans /api/get_classroom_details, mais certaines
  // réponses le donnent directement sous forme de chaîne.
  const grade = classroom.grade;
  if (grade && typeof grade === "object") {
    const named = pick(grade, ["name"]);
    if (named) return named;
  } else if (typeof grade === "string" && grade.trim()) {
    return grade.trim();
  }

  return pick(classroom, ["grade_code"]);
}

/**
 * Classe de l'élève, dans la forme employée partout ailleurs dans
 * l'application : « 5ème BC / TI » — niveau, numéro de salle, puis spécialité
 * après une barre oblique (voir l'en-tête de la fiche de classe).
 *
 * Chaque partie n'apparaît que si elle est renseignée : une classe sans
 * spécialité sort « 5ème BC », sans séparateur orphelin.
 */
function classroomLabel(classroom) {
  if (!classroom) return "";

  const grade = [gradeLabel(classroom), pick(classroom, ["number"])]
    .filter(Boolean)
    .join(" ");
  const speciality = pick(classroom, ["speciality_code"]);

  return [grade, speciality].filter(Boolean).join(" / ").trim();
}

/** Adresse composée des seuls champs réellement renseignés. */
function schoolAddress(school) {
  if (!school) return "";
  const box = pick(school, ["po_box"]);
  const place = [pick(school, ["hood"]), pick(school, ["town"]), pick(school, ["region"])]
    .filter(Boolean)
    .join(", ");
  return [box ? `B.P. ${box}` : "", place].filter(Boolean).join(" — ");
}

/**
 * Numéro du document.
 *
 * Construit sur une donnée stable (identifiant du versement, à défaut celui de
 * la tranche puis le matricule) : réimprimer un reçu redonne exactement le même
 * numéro, ce qu'un compteur aléatoire ne garantirait pas.
 */
function buildNumber(prefix, year, seed) {
  const clean = String(seed || "").replace(/[^A-Za-z0-9]/g, "");
  const tail = clean ? clean.slice(-6).toUpperCase() : "";
  return [prefix || "REC", year, tail].filter(Boolean).join("-");
}

/** Total réellement versé sur une tranche. */
export function paidOnPayment(payment) {
  const splits = (payment && payment.splits) || [];
  if (Array.isArray(splits) && splits.length) {
    return splits.reduce((total, split) => total + num(split, ["amount", "amount_paid"]), 0);
  }

  const recorded = num(payment, ["splits_total_amount", "paid_amount"]);
  if (recorded) return recorded;

  /*
   * Tranche soldée sans aucun versement détaillé : elle a été réglée en une
   * fois, hors du mécanisme de paiement partiel qui, lui, produit des splits.
   * Le montant versé est alors celui de la tranche — sans quoi le reçu
   * sortirait avec « Payé : 0 » sur une tranche pourtant réglée.
   */
  if (String((payment && payment.status) || "").toLowerCase() === "settled") {
    return num(payment, ["amount"]);
  }
  return 0;
}

/** Une tranche est-elle intégralement réglée ? */
export function isPaymentSettled(payment) {
  if (!payment) return false;
  if (String(payment.status || "").toLowerCase() === "settled") return true;
  const due = num(payment, ["amount"]);
  return due > 0 && paidOnPayment(payment) >= due;
}

/**
 * Un premier versement a-t-il été encaissé sur la scolarité ?
 *
 * Suffit à délivrer un reçu : dès qu'une somme est entrée, la famille a de
 * quoi justifier, sans attendre que tout soit soldé. Le document porte alors
 * la situation du moment — versé à ce jour, et ce qui reste dû.
 *
 * On regarde les tranches elles-mêmes, pas `payment_status` : ce statut
 * global calculé côté serveur ne dit pas si de l'argent est déjà rentré.
 */
export function hasTuitionPayment(payments) {
  return (payments || [])
    .filter((payment) => payment && !payment.is_other_payment)
    .some((payment) => paidOnPayment(payment) > 0);
}

/**
 * L'élève est-il à jour de TOUTE sa scolarité ?
 *
 * On s'appuie sur les tranches elles-mêmes plutôt que sur `payment_status`,
 * statut global calculé côté serveur qui ne dit pas si chaque échéance est
 * soldée. Les autres paiements (`is_other_payment`) sont exclus : ils ne
 * relèvent pas de la scolarité.
 */
export function isTuitionFullySettled(payments) {
  const tuition = (payments || []).filter((payment) => payment && !payment.is_other_payment);
  if (!tuition.length) return false;
  return tuition.every(isPaymentSettled);
}

/**
 * Construit le reçu d'UN versement précis.
 *
 * @param {object} options.receipt    modèle configuré par l'établissement
 * @param {object} options.student    élève, tel que renvoyé par l'API
 * @param {object} options.classroom  classe { grade_code, speciality_code }
 * @param {object} options.school     schoolProfile
 * @param {object} options.payment    tranche concernée
 * @param {object} options.split      versement concerné (null → reçu global)
 * @param {number} options.splitIndex rang du versement dans la tranche
 * @param {number} options.paymentIndex rang de la tranche (1-based), utilisé
 *                                    quand l'API ne renvoie pas son numéro
 */
export function resolveReceipt({
  receipt,
  student,
  classroom,
  school,
  payment,
  split,
  splitIndex = 0,
  paymentIndex = 0,
  currency = "",
  locale = currentLocale(),
}) {
  const model = receipt || defaultReceipt(locale);
  const missing = [];

  const require = (value, key) => {
    if (!value) missing.push(key);
    return value;
  };

  /* --- Élève --------------------------------------------------------- */
  const studentName = [pick(student, ["first_name"]), pick(student, ["last_name"])]
    .filter(Boolean)
    .join(" ")
    .trim();

  /* --- Montants ------------------------------------------------------ */
  const due = num(payment, ["amount"]);
  const totalPaid = paidOnPayment(payment);

  /*
   * Reçu d'un versement : on imprime CE versement, et le solde tel qu'il
   * était juste après lui. Réimprimer un ancien reçu redonne donc le solde
   * de l'époque, pas celui d'aujourd'hui — c'est ce qu'attend un justificatif.
   */
  let amountPaid = totalPaid;
  let cumulative = totalPaid;
  if (split) {
    amountPaid = num(split, ["amount", "amount_paid"]);
    const splits = (payment && payment.splits) || [];
    cumulative = splits
      .slice(0, splitIndex + 1)
      .reduce((total, item) => total + num(item, ["amount", "amount_paid"]), 0);
  }
  const balance = Math.max(due - cumulative, 0);

  /* --- Versement -----------------------------------------------------
   *
   * Le POST /api/set_partial_payment ENVOIE `operator_tx_id`, `amount_paid` et
   * `processed_on`. Mais les versements RELUS depuis /api/student/:id ne
   * portent pas tous ces noms-là : la date et le montant y sont `on` et
   * `amount`, et l'identifiant de transaction arrive parfois dans une
   * référence déjà composée. Les deux jeux de noms sont donc lus.
   */
  const source = split || payment || {};
  const paidOn = pick(source, ["on", "processed_on", "paid_on", "created_on", "date"]);

  const reference = pick(source, ["reference", "operator_tx_id", "transaction_id", "tx_id"]);
  let transactionId = pick(source, ["operator_tx_id", "transaction_id", "tx_id"]);

  /*
   * Référence déjà composée « OPÉRATEUR — TXID » : seul l'identifiant de
   * transaction est retenu, l'opérateur ne figure plus sur le reçu. Les trois
   * tirets courants sont acceptés, la référence ayant pu être composée à un
   * autre endroit de l'application.
   */
  const separator = reference && reference.match(/\s[—–-]\s/);
  if (separator && !transactionId) {
    transactionId = reference.split(separator[0])[1].trim();
  } else if (!transactionId) {
    transactionId = reference;
  }

  /* --- Type de document ----------------------------------------------
   *
   * Une tranche de scolarité est identifiée par SON NUMÉRO, exactement comme
   * dans l'énumération des tranches de la fiche de classe (« Tranche 1 »,
   * « Tranche 2 »…). Le libellé ne le remplace pas : il vaut souvent la même
   * chose pour toutes les tranches d'une même configuration, et le reçu ne
   * disait alors pas laquelle avait été réglée.
   *
   * Les autres paiements, eux, n'ont pas de rang : c'est leur libellé qui les
   * identifie (« Frais d'examen »…).
   */
  const label = pick(payment, ["label"]);
  let type;
  if (payment && payment.is_other_payment) {
    type = label || (locale === "en" ? "Other payment" : "Autre paiement");
  } else {
    // `number` vient de l'API ; à défaut, le rang de la tranche dans la liste
    // de l'élève, qui suit le même ordre que l'énumération de la classe.
    const rank = pick(payment, ["number"]) || (paymentIndex ? String(paymentIndex) : "");
    const word = locale === "en" ? "Instalment" : "Tranche";
    type = rank ? `${word} ${rank}` : word;
  }

  /* --- Numéro du document -------------------------------------------- */
  const year = new Date().getFullYear();
  const seed =
    pick(source, ["id", "payment_id"]) ||
    pick(payment, ["id"]) ||
    `${pick(student, ["registration_number"])}${splitIndex}`;

  return {
    /* Habillage, repris tel quel du modèle configuré. */
    template: model.template,
    format: model.format,
    palette: model.palette,
    // Recalculés à chaque émission, jamais relus de la configuration.
    title: defaultTitle(locale),
    footer_note: defaultFooterNote(locale),
    show_logo: model.show_logo,
    show_school_contacts: model.show_school_contacts,
    show_signature: model.show_signature,
    currency,
    locale,

    /* École */
    school: {
      name: require(pick(school, ["name"]), "ecole"),
      logo: pick(school, ["logo"]),
      address: schoolAddress(school),
      phone: pick(school, ["phone"]),
      email: pick(school, ["email"]),
    },

    /* Document */
    number: buildNumber(RECEIPT_PREFIX, year, seed),
    type,
    issued_on: formatDate(paidOn, locale) || formatDate(new Date().toISOString(), locale),
    reference: transactionId,

    /* Élève */
    student_name: require(studentName, "eleve"),
    registration_number: require(pick(student, ["registration_number"]), "matricule"),
    classroom: require(classroomLabel(classroom), "classe"),

    /* Montants */
    amount_due: due,
    amount_paid: amountPaid,
    balance,
    settled: balance <= 0,

    /* Signature */
    signatory_role: model.signatory_role,
    signatory_name: model.signatory_name,

    /* Champs réellement absents, signalés avant impression. */
    missing,
  };
}

/**
 * Reçu de la scolarité : la situation financière de l'élève, pas une tranche.
 *
 * Les montants couvrent l'ENSEMBLE des tranches appliquées à l'élève — total
 * dû, total versé, et le reste à payer qui en découle. C'est ce qu'attend une
 * famille : un seul document qui dit où elle en est, et non un reçu par
 * échéance dont il faudrait faire la somme.
 *
 * Émis dès le premier versement, sans attendre que tout soit soldé. La date
 * d'émission est celle du dernier versement enregistré.
 */
export function resolveGlobalReceipt({
  receipt,
  student,
  classroom,
  school,
  payments,
  currency = "",
  locale = currentLocale(),
}) {
  const tuition = (payments || []).filter((payment) => payment && !payment.is_other_payment);

  const due = tuition.reduce((total, payment) => total + num(payment, ["amount"]), 0);
  const paid = tuition.reduce((total, payment) => total + paidOnPayment(payment), 0);

  /* Dernier versement enregistré, toutes tranches confondues. */
  const splits = tuition.flatMap((payment) => payment.splits || []);
  const last = splits.length ? splits[splits.length - 1] : null;

  const resolved = resolveReceipt({
    receipt,
    student,
    classroom,
    school,
    payment: tuition[0] || {},
    split: last,
    splitIndex: 0,
    currency,
    locale,
  });

  const settled = paid >= due;

  return {
    ...resolved,
    /* Le libellé suit l'état : « complète » sur un reçu qui laisse un reste
       à payer serait un contresens. */
    type: settled
      ? locale === "en"
        ? "Full tuition"
        : "Scolarité complète"
      : locale === "en"
      ? "Tuition"
      : "Scolarité",
    amount_due: due,
    amount_paid: paid,
    balance: Math.max(due - paid, 0),
    settled,
    number: buildNumber(
      RECEIPT_PREFIX,
      new Date().getFullYear(),
      `${pick(student, ["registration_number"])}TOTAL`
    ),
  };
}
